import { WOODLAND_BACKDROPS } from '../../../game/effects/atmosphere/silhouettes/woodlandBackdrops';
import type { AtmospherePreset } from '../presets.config';
import { ATMOSPHERE_PALETTE as P } from '../palette';
import { breathingGlowBlobs, gustingWeather, multiPlaneParallax, biomeCloser } from '../presetBuilders';

/** Autumn / Amber Grove — a warm harvest-time forest at GOLDEN HOUR: a dusky-orange sky
 *  with a low golden sun, slanting amber god-ray shafts, drifting firefly motes, a cozy
 *  warm glow that breathes, a shimmering warm bed, falling leaves on a gentle wind
 *  (amber → crimson), a hearth-warm floor flicker, a layered autumn-tree treeline, soft
 *  golden haze, and a warm amber grade. Fully AUTUMNAL orange/red/gold — distinct from the
 *  cozy forest's mint/amber: this is harvest, not enchanted. */
export const AUTUMN: AtmospherePreset = {
  id: 'autumn',
  layers: [
    // Golden-hour sky — dusky burnt-orange zenith → bright amber-gold horizon, so the
    // warm tree silhouettes below read as real depth against a lit sky.
    { kind: 'skyGradient', from: 0x7a3a1e, to: 0xe6a85a },
    // Low golden-hour sun + broad warm halo — the light source the rays + glow read from.
    { kind: 'celestial', atX: 0.74, atY: 0.2, color: 0xffcf7a, haloColor: P.amber, size: 92, haloScale: 3.2, coreAlpha: 0.82, haloAlpha: 0.2 },
    // Slanting golden shafts — wide, warm, gently breathing.
    { kind: 'godRays', count: 4, color: P.amber, widthMin: 64, widthMax: 150, height: 540, tiltDeg: 12, periodMinMs: 8000, periodMaxMs: 15000, alphaMin: 0.10, alphaMax: 0.34 },
    // Drifting firefly motes — warm gold dust filtering through the canopy.
    { kind: 'fireflies', count: 24, colors: [P.firefly, P.amber, P.torch], sizeMin: 5, sizeMax: 14, driftPx: 40, periodMinMs: 5500, periodMaxMs: 12000, alphaMin: 0.18, alphaMax: 0.7 },
    // Sun bloom (top) + cozy hearth-warm ground glow (bottom) — the warmth swelling slowly.
    breathingGlowBlobs(
      [
        { color: P.sunHaze, atX: 0.74, atY: 0.18, size: 440, alpha: 0.09 },
        { color: P.torch, atX: 0.5, atY: 0.96, size: 580, alpha: 0.12 },
      ],
      { periodMs: 5600, min: 0.78, max: 1.3 },
    ),
    // Warm shimmer bed.
    { kind: 'lightField', count: 13, palette: [P.amber, P.torch, P.firefly], sizeMin: 110, sizeMax: 280, alphaMin: 0.05, alphaMax: 0.16, driftRadius: 64, periodMinMs: 6500, periodMaxMs: 12500 },
    // Falling leaves — large, slow, amber → crimson, drifting on a gentle wind, density gusting.
    gustingWeather(
      { mode: 'fall', color: P.amber, colorEnd: 0x8a2418,
        size: 13, frequencyMs: 150, lifespanMs: 5200, speed: 16, gravityY: 10, windX: 30, alphaEnd: 0.6 },
      { periodMs: 8000, min: 0.5, max: 1.5, phase: 0.3 },
    ),
    // Cozy hearth-warm floor flicker (harvest-time warmth).
    { kind: 'flicker', color: P.torch, size: 300, baseAlpha: 0.07, flickerAmp: 0.05, hzMin: 2, hzMax: 5, anchors: [{ atX: 0.22, atY: 0.92 }, { atX: 0.54, atY: 0.95 }, { atX: 0.82, atY: 0.92 }] },
    // Layered autumn treeline (PX) against the golden sky — far/mid/near, LIT warm brown-orange
    // (not dark silhouettes). Far is tallest + sways gently; near is shortest + most opaque.
    ...multiPlaneParallax([
      { svg: WOODLAND_BACKDROPS.autumn.far, shape: 'peaks', color: 0x7a4423, speed: 0.04, height: 320, alpha: 0.72, detail: 0.8, sway: { ampPx: 10, periodMs: 28000 } },
      { svg: WOODLAND_BACKDROPS.autumn.mid, shape: 'ridge', color: 0x52301a, speed: 0.10, height: 230, alpha: 0.86, detail: 1.1 },
      { svg: WOODLAND_BACKDROPS.autumn.near, shape: 'peaks', color: 0x35200e, speed: 0.18, height: 160, alpha: 0.95, detail: 1.0 },
    ]),
    // Soft golden haze drifting through the grove.
    { kind: 'fog', color: 0xc88a4a, bands: 4, alphaMin: 0.03, alphaMax: 0.10, driftPx: 46, periodMinMs: 9000, periodMaxMs: 16000 },
    ...biomeCloser({
      vignette: { color: 0x2a1408, edgeAlpha: 0.32, washColor: 0xff9a3c, washAlpha: 0.05 },
      // Warm amber grade — a gentle 'add' golden-hour glow (subtle, keeps the silhouettes crisp).
      grade: { top: P.amber, bottom: 0x9c4a1e, strength: 0.1, blend: 'add' },
    }),
  ],
};
